import React, { useState } from "react";
import Button from "../Buttons/Button";
import FormFieldSet from "../FormFields/FormFieldSet";

const MultiLevelBuilder = ({
  config,
  onSchedule = () => {},
  onViewClick,
  onTemplateAdd,
}) => {
  const MAX_DEPTH = 3;
  const maxSubSteps = config?.maxSubSteps || 3;
  const campaignTypes = config?.campaignTypes || [];

  const [steps, setSteps] = useState([
    {
      id: "1",
      level: 1,
      name: "Step-1",
      scheduled: false,
      canAddSub: true,
      data: {
        campaignName: "",
        list: "",
        campaignType: campaignTypes[0]?.value || "Email",
        source: "",
        sourceId: "",
        template: [], // Changed to array

        configurationName: "",
        count: "",
        dateTime: "",
        selectAccount: "",
      },
    },
  ]);

  const getOptions = (campaignType, optionType) => {
    if (config.getOptions) {
      return config.getOptions(campaignType, optionType);
    }
    return [];
  };

  const getFieldLabels = (campaignType) => {
    if (config.getLabels) {
      return config.getLabels(campaignType);
    }
    return { source: "Source", id: "ID" };
  };

  const getFieldConfig = (campaignType) => {
    if (config.getFields) {
      return config.getFields(campaignType);
    }
    return [];
  };

  const getGridColumns = (step) => {
    const fieldConfig = getFieldConfig(step.data.campaignType);
    const commonFields = 3;
    const dynamicFields = fieldConfig.filter(
      (f) => f.name !== "campaignName"
    ).length;
    const totalColumns = commonFields + dynamicFields + 1;
    return totalColumns;
  };

  const getCampaignTypeConfig = (type) => {
    return campaignTypes.find((ct) => ct.type === type) || {};
  };

  const sortStepsList = (stepsArray) => {
    return stepsArray.sort((a, b) => {
      const aParts = a.id.split(".").map(Number);
      const bParts = b.id.split(".").map(Number);

      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aVal = aParts[i] || 0;
        const bVal = bParts[i] || 0;
        if (aVal !== bVal) return aVal - bVal;
      }
      return 0;
    });
  };

  const countSubSteps = (parentId) => {
    return steps.filter((s) => {
      const sId = s.id;
      const pId = parentId;
      if (sId.startsWith(pId + ".")) {
        const remaining = sId.substring(pId.length + 1);
        return !remaining.includes(".");
      }
      return false;
    }).length;
  };

  // const addStep = (parentId) => {
  //   const parent = steps.find((s) => s.id === parentId);
  //   if (!parent || !parent.scheduled) return;

  //   const subCount = countSubSteps(parentId);
  //   if (subCount >= maxSubSteps) return;

  //   const newId = `${parentId}.${subCount + 1}`;
  //   const parts = newId.split(".");

  //   if (parts.length > MAX_DEPTH) return;

  //   const incrementFirstSegment = (id) => {
  //     const parts = id.split(".");
  //     parts[0] = String(Number(parts[0]) + 1);
  //     return parts.join(".");
  //   };

  //   const newName = `Step-${incrementFirstSegment(newId)}`;

  //   const newStep = {
  //     id: newId,
  //     level: parts.length,
  //     name: newName,
  //     scheduled: false,
  //     canAddSub: parts.length < MAX_DEPTH,
  //     parentCampaignType: parent.data.campaignType,
  //     data: {
  //       campaignName: "",
  //       campaignType: "",
  //       previousStatus: "",

  //       source: "",
  //       sourceId: "",
  //       template: [], // Changed to array

  //       configurationName: "",
  //       count: "",
  //       dateTime: "",
  //       selectAccount: "",
  //     },
  //   };

  //   setSteps([...steps, newStep]);
  // };
  const addStep = (parentId) => {
    const parent = steps.find((s) => s.id === parentId);
    if (!parent || !parent.scheduled) return;

    const subCount = countSubSteps(parentId);
    if (subCount >= maxSubSteps) return;

    const newId = `${parentId}.${subCount + 1}`;
    const parts = newId.split(".");

    if (parts.length > MAX_DEPTH) return;

    const parentTypeConfig = getCampaignTypeConfig(parent.data.campaignType);

    const newStep = {
      id: newId,
      level: parts.length,
      name: `Step-${newId}`,
      scheduled: false,
      canAddSub: parts.length < MAX_DEPTH,
      parentCampaignType: parent.data.campaignType,
      data: {
        campaignType: "",
        previousStatus: "",
        ...Object.keys(campaignTypes[0]?.fields || {}).reduce((acc, key) => {
          acc[key] = "";
          return acc;
        }, {}),
      },
    };

    setSteps([...steps, newStep]);
  };
  const scheduleStep = (stepId) => {
    const step = steps.find((s) => s.id === stepId);
    if (step) {
      setSteps(
        steps.map((s) => (s.id === stepId ? { ...s, scheduled: true } : s))
      );
      onSchedule(step);
    }
  };

  const updateStepData = (stepId, field, value) => {
    setSteps(
      steps.map((s) => {
        if (s.id === stepId) {
          const newData = { ...s.data, [field]: value };

          if (field === "campaignType") {
            newData.source = "";
            newData.sourceId = "";
            newData.template = []; // Reset to empty array
            newData.configurationName = "";
            newData.count = "";
            newData.dateTime = "";
            newData.selectAccount = "";
          }
          return { ...s, data: newData };
        }
        return s;
      })
    );
  };

  const isFormValid = (step) => {
    const { data } = step;
    const fieldConfig = getFieldConfig(data.campaignType);

    if (!data.campaignName) return false;

    if (step.level === 1 && !data.list) return false;

    if (step.level > 1 && !data.previousStatus) return false;

    if (!data.campaignType) return false;

    for (const field of fieldConfig) {
      if (field.name === "campaignName") continue;

      // Special check for template array
      if (field.name === "template") {
        if (!Array.isArray(data[field.name]) || data[field.name].length === 0) {
          return false;
        }
        continue;
      }

      if (!data[field.name]) return false;
    }

    return true;
  };

  const handleOpenDrawer = (stepId, clickedTemplate) => {
    const step = steps.find((s) => s.id === stepId);
    if (!step) return;

    const templates = getOptions(step.data.campaignType, "templates");

    if (onViewClick) {
      onViewClick(stepId, templates, step.data.campaignType, clickedTemplate);
    }
  };

  // Handle template addition from parent
  React.useEffect(() => {
    if (onTemplateAdd) {
      onTemplateAdd((stepId, templateValue) => {
        setSteps((prevSteps) =>
          prevSteps.map((s) => {
            if (s.id === stepId) {
              const currentTemplates = Array.isArray(s.data.template)
                ? s.data.template
                : [];
              // Add template only if it's not already selected
              if (!currentTemplates.includes(templateValue)) {
                return {
                  ...s,
                  data: {
                    ...s.data,
                    template: [...currentTemplates, templateValue],
                  },
                };
              }
            }
            return s;
          })
        );
      });
    }
  }, []);

  const renderCampaignFields = (step) => {
    const isFirstLevel = step.level === 1;
    const indent = (step.level - 1) * 24;

    const availableStatuses = step.parentCampaignType
      ? getOptions(step.parentCampaignType, "statuses")
      : [];

    const fieldLabels = getFieldLabels(step.data.campaignType);
    const fieldConfig = getFieldConfig(step.data.campaignType);
    const gridColumns = getGridColumns(step);

    return (
      <div className="mb-4" style={{ marginLeft: `${indent}px` }}>
        <div className="flex items-center gap-2 mb-2">
          {!isFirstLevel && (
            <div className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0"></div>
          )}
          <div
            className={`flex-1 flex items-center justify-between px-3 py-2 ${
              isFirstLevel
                ? "bg-gray-50 border-gray-200"
                : "bg-primary-50 border-primary-200"
            } border rounded`}
          >
            <span className="font-medium text-sm text-gray-700">
              {step.name}
            </span>
          </div>

          <Button
            type="icon"
            icon={"Add"}
            className="!rounded-none"
            variant="outlined"
            color={isFirstLevel ? "black" : "primary"}
            onClick={() => addStep(step.id)}
            disabled={
              !step.scheduled ||
              !step.canAddSub ||
              countSubSteps(step.id) >= maxSubSteps
            }
          />
        </div>

        <div
          className="gap-3 py-2 px-4"
          style={{
            display: "grid",
            // gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",

          }}
        >
          <FormFieldSet
            type="text"
            value={step.data.campaignName}
            title="Campaign Name"
            onChange={(value) => updateStepData(step.id, "campaignName", value)}
            placeholder="Campaign Name"
            shadow={true}
            disabled={step.scheduled}
          />

          {isFirstLevel ? (
            <FormFieldSet
              type="select"
              onChange={(value) => updateStepData(step.id, "list", value)}
              options={config?.lists || []}
              value={step.data.list}
              title="Select the list"
              placeholder="Select"
              shadow={true}
              disabled={step.scheduled}
              showBadges={true}
              badgeSize={"xs-sm"}
            />
          ) : (
            <FormFieldSet
              type="select"
              shadow={true}
              title="Previous Campaign Status"
              value={step.data.previousStatus}
              onChange={(value) =>
                updateStepData(step.id, "previousStatus", value)
              }
              options={availableStatuses}
              placeholder="Select"
              disabled={step.scheduled}
            />
          )}

          <FormFieldSet
            type="select"
            title="Campaign Type"
            value={step.data.campaignType}
            onChange={(value) => updateStepData(step.id, "campaignType", value)}
            options={campaignTypes}
            placeholder="Select"
            shadow={true}
            disabled={step.scheduled}
          />

          {fieldConfig.map((field) => {
            if (field.name === "campaignName") return null;

            let fieldOptions = [];
            let fieldLabel = field.label;

            if (field.name === "source") {
              fieldLabel = fieldLabels.source;
              fieldOptions = getOptions(step.data.campaignType, "sources");
            } else if (field.name === "sourceId") {
              fieldLabel = fieldLabels.id;
              fieldOptions = getOptions(step.data.campaignType, "sourceIds");
            } else if (field.name === "template") {
              fieldOptions = getOptions(step.data.campaignType, "templates");
            } else if (field.name === "selectAccount") {
              fieldOptions = getOptions(
                step.data.campaignType,
                "selectAccount"
              );
            }

            return (
              <FormFieldSet
                key={field.name}
                type={field.name === "template" ? "multiSelect" : field.type}
                style={field.name === "template" ? "chip" : "normal"}
                value={step.data[field.name]}
                title={fieldLabel}
                placeholder={field.placeholder}
                onChange={(value) => updateStepData(step.id, field.name, value)}
                disabled={!step.data.campaignType || step.scheduled}
                options={field.type === "select" ? fieldOptions : undefined}
                shadow={true}
                showViewButton={field.name === "template"}
                onViewClick={(clickedValue) =>
                  handleOpenDrawer(step.id, clickedValue)
                }
              />
            );
          })}

          <div className="flex items-end px-3">
            <Button
              title={"Schedule"}
              onClick={() => scheduleStep(step.id)}
              disabled={!isFormValid(step) || step.scheduled}
              className="w-full"
            />
          </div>
        </div>
      </div>
    );
  };

  const sortedSteps = sortStepsList([...steps]);

  return (
    <div className="w-full py-4 px-4">
      {sortedSteps.map((step) => (
        <div key={step.id}>{renderCampaignFields(step)}</div>
      ))}
    </div>
  );
};

export default MultiLevelBuilder;
